// admin/AdminApp.tsx — top-level for the admin portal.
//
// Auth flow (post socket-rework):
//   1. No bearer → <AdminLogin />.
//   2. Bearer present → wrap the tree in <Provider> and a <SocketConnector>
//      that opens a socket.io connection with the JWT in the handshake.
//   3. Socket handshake either:
//        - succeeds → server emits sync:initial; we hydrate Redux, render the
//          shell, and start receiving live `event` broadcasts.
//        - fails with 'admin_required' → bounce to https://www.emberhollow.ai
//          (the player surface), same UX as the old REST-gated flow.
//        - fails for any other reason → connection slice goes to fatal,
//          AdminShell renders a blocking overlay.
//
// This file is mounted from main.tsx ONLY when the hostname resolves to the
// admin subdomain (or the ?admin=1 dev escape hatch). The player tree never
// sees it.

import { useEffect, useRef, useState } from "react";
import { Provider } from "react-redux";
import type { Socket } from "socket.io-client";
import { styles } from "./styles";
import AdminLogin from "./AdminLogin";
import AdminShell from "./AdminShell";
import {
  getAccessToken,
  clearAuth,
} from "../auth/tokens";
import {
  store,
  connectionActions,
  dashboardActions,
  scenariosActions,
  eventsActions,
  ideasActions,
  sfxActions,
  usersActions,
} from "./store";
import { createSocket, saveCursor, loadCursor, clearCursor, emitCatchup } from "./socket";
import { routeEvent } from "./socket/reducerBridge";
import type { AdminEvent, AdminSync } from "./store/types";

const PLAYER_URL = "https://www.emberhollow.ai";

export default function AdminApp() {
  const [token, setToken] = useState<string | null>(() => getAccessToken());

  // The login screen and the connected tree are split at the token boundary.
  // Once a token exists we mount the Provider + SocketConnector; the rest
  // happens via Redux.
  if (!token) {
    return (
      <AdminLogin
        onSignedIn={() => {
          const fresh = getAccessToken();
          if (fresh) setToken(fresh);
        }}
      />
    );
  }

  return (
    <Provider store={store}>
      <SocketConnector
        token={token}
        onSignedOut={() => {
          // Clear cursor + Redux + tokens, then back to the login screen.
          // (Cursor is keyed by adminId — we read it before the slice resets.)
          const adminId = store.getState().dashboard.admin?.id;
          if (adminId) clearCursor(adminId);
          store.dispatch(connectionActions.reset());
          store.dispatch(dashboardActions.clear());
          store.dispatch(scenariosActions.clear());
          store.dispatch(usersActions.clear());
          store.dispatch(eventsActions.clear());
          store.dispatch(ideasActions.clear());
          store.dispatch(sfxActions.clear());
          clearAuth();
          setToken(null);
        }}
      />
    </Provider>
  );
}

interface SocketConnectorProps {
  token: string;
  onSignedOut: () => void;
}

/**
 * Owns the live socket. Wires every inbound message to the right reducer.
 * Renders <AdminShell /> once a connection has been established at least
 * once; transient drops keep the shell mounted but flip the connection slice
 * to 'reconnecting' so AdminShell can show its banner.
 */
function SocketConnector({ token, onSignedOut }: SocketConnectorProps) {
  // Track whether sync:initial has landed at least once; before that we render
  // a connecting spinner instead of the shell (which expects state.admin).
  const [ready, setReady] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    let cancelled = false;
    const sock = createSocket(token);
    socketRef.current = sock;
    store.dispatch(connectionActions.connecting());

    // ── connect_error: includes admin_required, invalid_token, etc. ──
    sock.on("connect_error", (err: Error) => {
      const msg = err?.message ?? "connect_error";
      console.error("[admin-socket] connect_error:", msg);
      if (msg === "admin_required") {
        // Non-admins bounce. Don't keep retrying — clear the token first so
        // we don't loop on reload.
        clearAuth();
        if (typeof window !== "undefined") {
          window.location.href = PLAYER_URL;
        }
        return;
      }
      if (msg === "invalid_token" || msg === "missing_token" || msg === "user_not_found") {
        // Token is broken — drop to the login screen.
        if (cancelled) return;
        sock.disconnect();
        onSignedOut();
        return;
      }
      // Any other handshake error — surface in the banner so the operator
      // sees it. The auto-reconnect loop will keep trying.
      if (!cancelled) {
        store.dispatch(connectionActions.reconnecting(msg));
      }
    });

    // ── disconnect: socket.io's own reconnect kicks in automatically ──
    sock.on("disconnect", (reason: string) => {
      if (cancelled) return;
      console.warn("[admin-socket] disconnected:", reason);
      // Per socket.io docs, "io server disconnect" means the server forcibly
      // disconnected us — auto-reconnect is OFF in that case. We surface as
      // fatal so the operator can refresh.
      if (reason === "io server disconnect") {
        store.dispatch(connectionActions.fatal("server_disconnect"));
        return;
      }
      store.dispatch(connectionActions.reconnecting(reason));
    });

    sock.on("reconnect_attempt", () => {
      if (cancelled) return;
      store.dispatch(connectionActions.reconnecting(undefined));
    });

    // ── sync:initial ──
    // First connect AND every successful reconnect both fire 'connect'.
    // The server emits sync:initial unconditionally on the connection
    // handler, so we set it up here once and the listener handles both.
    sock.on("sync:initial", (payload: AdminSync) => {
      if (cancelled) return;
      try {
        store.dispatch(dashboardActions.setAdmin(payload.admin));
        store.dispatch(dashboardActions.set(payload.dashboard));
        store.dispatch(scenariosActions.set(payload.scenarios));
        // Older servers may omit serverVersion → fall back to "—".
        store.dispatch(
          dashboardActions.setServerVersion(payload.serverVersion ?? "—"),
        );
        store.dispatch(connectionActions.connected(payload.cursor));
        saveCursor(payload.admin.id, payload.cursor);
        setReady(true);
      } catch (err) {
        console.error("[admin-socket] sync:initial handler error:", err);
        store.dispatch(
          connectionActions.fatal(
            `sync_initial_failed: ${err instanceof Error ? err.message : String(err)}`,
          ),
        );
      }
    });

    // ── connect: on reconnect (not first connect), try sync:catchup so the
    //    dashboard snaps to fresh state immediately. The server also emits
    //    sync:initial unconditionally, so this is belt + braces — whichever
    //    arrives first wins, both update the dashboard.
    let isFirstConnect = true;
    sock.on("connect", () => {
      if (cancelled) return;
      if (isFirstConnect) {
        isFirstConnect = false;
        return;
      }
      const admin = store.getState().dashboard.admin;
      if (!admin) return;
      const cursor = loadCursor(admin.id);
      emitCatchup(sock, { cursor })
        .then((ack) => {
          if (cancelled) return;
          if (!ack.ok) {
            console.error("[admin-socket] sync:catchup not ok:", ack.error);
            return;
          }
          store.dispatch(dashboardActions.set(ack.dashboard));
          store.dispatch(connectionActions.connected(ack.cursor));
          saveCursor(admin.id, ack.cursor);
        })
        .catch((err) => {
          console.error("[admin-socket] sync:catchup failed:", err);
        });
    });

    // ── event: live broadcasts ──
    sock.on("event", (evt: AdminEvent) => {
      if (cancelled) return;
      try {
        // Persist the cursor as the latest seen `at`. If a reconnect happens
        // before another sync:initial / sync:catchup, this is what we'll
        // hand the server.
        const admin = store.getState().dashboard.admin;
        if (admin) {
          saveCursor(admin.id, evt.at);
          store.dispatch(connectionActions.updateCursor(evt.at));
        }
        routeEvent(evt, store.dispatch);
      } catch (err) {
        console.error("[admin-socket] event handler error:", err);
      }
    });

    // ── error: socket-level non-handshake errors ──
    sock.on("error", (err: unknown) => {
      console.error("[admin-socket] socket error:", err);
    });

    // Kick off the connection.
    sock.connect();

    return () => {
      cancelled = true;
      // Removing listeners before disconnect avoids spurious 'reconnecting'
      // dispatches on unmount.
      sock.off("connect_error");
      sock.off("disconnect");
      sock.off("reconnect_attempt");
      sock.off("sync:initial");
      sock.off("connect");
      sock.off("event");
      sock.off("error");
      sock.disconnect();
      socketRef.current = null;
    };
  }, [token, onSignedOut]);

  if (!ready) {
    return (
      <div style={styles.loginShell}>
        <div style={styles.spinner}>Connecting to admin server…</div>
      </div>
    );
  }

  // Shell expects a non-null admin profile in Redux — at this point
  // sync:initial has landed at least once and dashboard.admin is populated.
  return (
    <AdminShell
      socket={socketRef}
      onSignOut={onSignedOut}
    />
  );
}
